import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Check, Layers, RefreshCw, Sparkles } from "lucide-react";

import { apiGet, apiPost } from "../../Api/Client.js";
import InfoTooltip from "../../Shared/InfoTooltip.jsx";
import { Button, Card, EmptyState, Field, Input, Page, PageHeader, Textarea } from "../../Ui";
import {
  bundleSummary, cardOutcome, decisionError, groupByBundle, parseHours, readableFacts, timeAgo,
} from "./proposalsViewModel.js";
import "./ProposalsScreen.css";

/* Suggested Tasks: what the AI helpers suggest, for a person to approve or reject.
 *
 * Each card is one suggestion (a new task, a status change, an end-of-day report) already
 * written in plain words by the server, and the server lists only the ones this person may decide.
 * On Approve the server does the work itself. The decision always carries the signed-in person,
 * decided on the server, so this screen never sends who is approving. Handled cards fold into a greyed one-line summary in place; nothing else moves.
 * Helper text is shown as plain text only.
 *
 * A helper can send several suggestions as one bundle (a sprint plan's tasks). They show as one group with
 * Approve all and Reject all; every card in it can still be decided on its own. The group asks the server
 * to decide exactly the cards that were waiting on screen, and the server answers for each card, so a card
 * that could not be created says so and the rest stand.
 */

const BASE = "/axon/proposals";
const NOT_WAITING = new Set(["not_found", "already_handled"]);

function Fact({ fact }) {
  return (
    <div className={`Proposal-Fact${fact.wide ? " Wide" : ""}`}>
      <dt>{fact.label}</dt>
      <dd>
        {fact.items.length > 0
          ? <ul>{fact.items.map((text) => <li key={text}>{text}</li>)}</ul>
          : fact.value}
      </dd>
    </div>
  );
}

function SkeletonCards() {
  return (
    <div className="Proposal-List" aria-busy="true" aria-label="Loading suggested tasks">
      {[0, 1, 2].map((n) => (
        <Card className="Proposal-Card Proposal-Skeleton" key={n}>
          <span className="ds-skeleton Skel-Line Wide" />
          <span className="ds-skeleton Skel-Line" />
          <span className="ds-skeleton Skel-Line Short" />
        </Card>
      ))}
    </div>
  );
}

function ProposalCard({ item, outcome, onOutcome }) {
  const [mode, setMode] = useState("idle"); // idle | hours | reject
  const [hours, setHours] = useState(item.estimated_hours == null ? "" : String(item.estimated_hours));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [problem, setProblem] = useState("");

  if (outcome) {
    return (
      <Card className={`Proposal-Card Proposal-Done ${outcome.kind}`} aria-live="polite">
        {outcome.kind === "Failed" ? <AlertCircle size={16} /> : <Check size={16} />}
        <span className="Proposal-Done-Text">
          <strong>{item.title}</strong> · {outcome.text}
        </span>
      </Card>
    );
  }

  const decide = async (verb) => {
    setProblem("");
    const body = {};
    if (verb === "approve" && mode === "hours") {
      const parsed = parseHours(hours);
      if (!parsed.ok) { setProblem(parsed.error); return; }
      if (parsed.value !== null && parsed.value !== item.estimated_hours) body.edited_hours = parsed.value;
    }
    if (verb === "reject" && note.trim()) body.note = note.trim();
    setBusy(verb);
    try {
      const answer = await apiPost(`${BASE}/${encodeURIComponent(item.id)}/${verb}/`, body);
      const done = verb === "approve" ? "Approved" : "Rejected";
      const text = verb === "approve" && item.kind === "task.create" && answer?.task_id
        ? `Approved · task #${answer.task_id} created` : done;
      onOutcome(item.id, { kind: done, text });
    } catch (error) {
      const gone = NOT_WAITING.has(error?.payload?.code);
      if (gone) onOutcome(item.id, { kind: "Gone", text: decisionError(error) });
      else setProblem(decisionError(error));
    } finally {
      setBusy("");
    }
  };

  const ago = timeAgo(item.created_at);
  const from = item.helper_label;
  const working = Boolean(busy);

  return (
    <Card className="Proposal-Card">
      <header className="Proposal-Head">
        <div>
          <span className="Proposal-Kind">{item.kind_label}</span>
          <h3>{item.title || "Untitled suggestion"}</h3>
        </div>
        <InfoTooltip title="AI reasoning" width={300}>
          <strong>Why you are seeing this.</strong> {from} suggested this
          {item.evidence_summary ? `: ${item.evidence_summary}` : "."} Nothing changes in anyone's work until you approve it.
        </InfoTooltip>
      </header>

      {item.description && <p className="Proposal-Description">{item.description}</p>}

      <dl className="Proposal-Facts">
        {readableFacts(item.facts, item.title).map((fact) => <Fact key={fact.label} fact={fact} />)}
      </dl>

      <p className="Proposal-Source">
        <Sparkles size={13} /> From {from}{ago ? ` · ${ago}` : ""}
      </p>

      {mode === "hours" && (
        <Field label="Hours for this task" className="Proposal-Edit--hours">
          <Input
            type="text" inputMode="decimal" value={hours} disabled={working}
            onChange={(event) => setHours(event.target.value)}
          />
        </Field>
      )}
      {mode === "reject" && (
        <Field label="Add a note (optional)">
          <Textarea
            className="ds-input" rows={2} maxLength={1000} value={note} disabled={working}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
      )}

      {problem && <p className="Proposal-Problem" role="alert">{problem}</p>}

      <div className="Proposal-Actions">
        {mode !== "reject" && (
          <>
            <Button variant="primary" disabled={working} onClick={() => decide("approve")}>
              {busy === "approve" ? "Approving…" : "Approve"}
            </Button>
            <Button disabled={working} onClick={() => { setProblem(""); setMode("reject"); }}>
              Reject
            </Button>
            {mode === "idle" && item.editable_hours && (
              <Button variant="link" disabled={working} onClick={() => { setProblem(""); setMode("hours"); }}>
                Change hours
              </Button>
            )}
          </>
        )}
        {mode === "reject" && (
          <>
            <Button variant="danger" disabled={working} onClick={() => decide("reject")}>
              {busy === "reject" ? "Rejecting…" : "Reject"}
            </Button>
            <Button disabled={working} onClick={() => { setProblem(""); setMode("idle"); }}>
              Keep it
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}

function BundleGroup({ block, outcomes, onOutcomes }) {
  const [confirm, setConfirm] = useState(""); // "" | approve | reject
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [summary, setSummary] = useState(null); // { text, failed }

  const waiting = block.items.filter((item) => !outcomes[item.id]);

  const decideAll = async (verb) => {
    setProblem("");
    setBusy(true);
    try {
      const answer = await apiPost(`${BASE}/bundle/${verb}/`, {
        helper: block.helper, bundle_id: block.bundleId, ids: waiting.map((item) => item.id),
      });
      onOutcomes(Object.fromEntries((answer.results || []).map((result) => [result.id, cardOutcome(verb, result)])));
      setSummary({ text: bundleSummary(verb, answer), failed: answer.failed > 0 });
      setConfirm("");
    } catch (error) {
      setProblem(decisionError(error));
    } finally {
      setBusy(false);
    }
  };

  const how = waiting.length === 1 ? "1 task" : `${waiting.length} tasks`;

  return (
    <section className="Proposal-Bundle" aria-label={block.title}>
      <header className="Proposal-Bundle-Head">
        <div>
          <span className="Proposal-Kind"><Layers size={12} /> Suggested together</span>
          <h2>{block.title}</h2>
          <p>{waiting.length > 0 ? `${how} waiting` : "All handled"}</p>
        </div>
        {waiting.length > 0 && (
          <div className="Proposal-Bundle-Actions">
            {confirm === "" && (
              <>
                <Button variant="primary" disabled={busy} onClick={() => { setProblem(""); setConfirm("approve"); }}>
                  Approve all
                </Button>
                <Button disabled={busy} onClick={() => { setProblem(""); setConfirm("reject"); }}>
                  Reject all
                </Button>
              </>
            )}
            {confirm !== "" && (
              <>
                <span className="Proposal-Bundle-Ask">
                  {confirm === "approve" ? `Create ${how}?` : `Reject ${how}?`}
                </span>
                <Button
                  disabled={busy} onClick={() => decideAll(confirm)}
                  variant={confirm === "approve" ? "primary" : "danger"}
                >
                  {busy ? "Working…" : confirm === "approve" ? "Yes, approve all" : "Yes, reject all"}
                </Button>
                <Button disabled={busy} onClick={() => { setProblem(""); setConfirm(""); }}>
                  Cancel
                </Button>
              </>
            )}
          </div>
        )}
      </header>
      {problem && <p className="Proposal-Problem" role="alert">{problem}</p>}
      {summary && (
        <p className={`Proposal-Bundle-Note${summary.failed ? " Failed" : ""}`} role="status">
          {summary.text}{summary.failed ? " The ones that did not go through are marked below." : ""}
        </p>
      )}
      <div className="Proposal-List">
        {block.items.map((item) => (
          <ProposalCard
            key={item.id} item={item}
            outcome={outcomes[item.id]} onOutcome={(id, outcome) => onOutcomes({ [id]: outcome })}
          />
        ))}
      </div>
    </section>
  );
}

export function ProposalsScreen() {
  const [state, setState] = useState("loading"); // loading | ready | error
  const [items, setItems] = useState([]);
  const [outcomes, setOutcomes] = useState({});
  const [problem, setProblem] = useState("");

  const load = useCallback(async () => {
    setState("loading");
    setProblem("");
    try {
      const payload = await apiGet(`${BASE}/`);
      setItems(Array.isArray(payload?.suggestions) ? payload.suggestions : []);
      setOutcomes({});
      setState("ready");
    } catch (error) {
      setProblem(decisionError(error));
      setState("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Outcomes arrive as { cardId: outcome }, one card or a whole bundle at a time.
  const addOutcomes = useCallback((added) => setOutcomes((current) => ({ ...current, ...added })), []);
  const setOutcome = useCallback((id, outcome) => addOutcomes({ [id]: outcome }), [addOutcomes]);
  const waiting = items.filter((item) => !outcomes[item.id]).length;
  const blocks = useMemo(() => groupByBundle(items), [items]);

  return (
    <Page className="Proposals-Screen">
      <PageHeader
        title="Suggested Tasks"
        subtitle={`What your AI helpers suggest. Nothing changes until you approve it.${state === "ready" && waiting > 0 ? ` ${waiting} waiting.` : ""}`}
        actions={(
          <Button size="sm" onClick={load} disabled={state === "loading"} leftIcon={<RefreshCw size={14} />}>
            Refresh
          </Button>
        )}
      />

      {state === "loading" && <SkeletonCards />}

      {state === "error" && (
        <Card title="We could not load the suggested tasks">
          <div className="Proposal-Error">
            <p>{problem}</p>
            <Button variant="primary" size="sm" onClick={load} leftIcon={<RefreshCw size={14} />}>
              Try again
            </Button>
          </div>
        </Card>
      )}

      {state === "ready" && items.length === 0 && (
        <EmptyState>
          <Check size={20} />
          <strong>Nothing waiting for you</strong>
        </EmptyState>
      )}

      {state === "ready" && items.length > 0 && (
        <div className="Proposal-List">
          {blocks.map((block) => (block.kind === "bundle" ? (
            <BundleGroup key={block.key} block={block} outcomes={outcomes} onOutcomes={addOutcomes} />
          ) : (
            <ProposalCard
              key={block.key} item={block.item}
              outcome={outcomes[block.item.id]} onOutcome={setOutcome}
            />
          )))}
        </div>
      )}
    </Page>
  );
}
